/**
 * Service API réutilisable pour les appels HTTP
 */

import { buildApiUrl } from '@/config/api'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  headers?: Record<string, string>
  body?: unknown
  params?: Record<string, string>
}

export interface ApiResponse<T> {
  data: T
  status: number
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Récupère le token d'authentification depuis le localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token')
}

/**
 * Construit les en-têtes de la requête (JSON par défaut).
 * Pour FormData, ne pas passer Content-Type pour laisser le navigateur définir multipart boundary.
 */
const buildHeaders = (customHeaders?: Record<string, string>, omitContentType = false): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...customHeaders,
  }
  if (!omitContentType) {
    headers['Content-Type'] = 'application/json'
  }

  // Ne pas écraser un Authorization déjà fourni (ex. token Clerk pour /auth/clerk)
  if (!headers.Authorization) {
    const token = getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  return headers
}

/**
 * Construit l'URL avec les paramètres de requête
 */
const buildUrl = (url: string, params?: Record<string, string>): string => {
  if (!params || Object.keys(params).length === 0) {
    return url
  }

  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `${url}?${queryString}` : url
}

/**
 * Redirige vers la page de login et nettoie le localStorage
 */
const redirectToLogin = () => {
  localStorage.removeItem('access_token')
  localStorage.removeItem('user')
  // Redirect to login page
  if (window.location.pathname !== '/') {
    window.location.href = '/'
  }
}

/**
 * Gère les erreurs de réponse
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json')

  if (!response.ok) {
    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      console.warn('🔒 Token expired or invalid - redirecting to login')
      redirectToLogin()
      throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401)
    }

    let errorMessage = `Erreur ${response.status}: ${response.statusText}`
    let errorData: unknown = null

    if (isJson) {
      try {
        errorData = await response.json()
        if (errorData && typeof errorData === 'object') {
          // Handle FastAPI validation errors (422)
          if (Array.isArray((errorData as { detail?: unknown }).detail)) {
            const details = (errorData as { detail: Array<{ msg?: string; loc?: unknown[] }> }).detail
            const messages = details
              .map((d) => {
                if (d.msg) {
                  const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : ''
                  return field ? `${field}: ${d.msg}` : d.msg
                }
                return null
              })
              .filter((msg): msg is string => msg !== null)
            if (messages.length > 0) {
              errorMessage = messages.join(', ')
            }
          } else if ((errorData as { detail?: unknown }).detail) {
            const detail = (errorData as { detail: unknown }).detail
            errorMessage =
              typeof detail === 'string'
                ? detail
                : Array.isArray(detail)
                  ? detail.join(', ')
                  : errorMessage
          } else if ((errorData as { message?: string }).message) {
            errorMessage = (errorData as { message: string }).message
          }
        }
      } catch {
        // Si le parsing JSON échoue, on garde le message par défaut
      }
    }

    throw new ApiError(errorMessage, response.status, errorData)
  }

  // Si la réponse est vide (204 No Content), retourner null
  if (response.status === 204) {
    return null as T
  }

  // Parser la réponse JSON si disponible
  if (isJson) {
    try {
      return await response.json()
    } catch (error) {
      throw new ApiError('Erreur lors du parsing de la réponse JSON', 500)
    }
  }

  // Si ce n'est pas du JSON, retourner le texte
  return (await response.text()) as T
}

/**
 * Effectue une requête HTTP vers l'API
 */
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const {
    method = 'GET',
    headers: customHeaders,
    body,
    params,
  } = options

  const url = buildApiUrl(endpoint)
  const fullUrl = buildUrl(url, params)
  const isFormData = body instanceof FormData
  const headers = buildHeaders(customHeaders, isFormData)

  const config: RequestInit = {
    method,
    headers,
  }

  if (body && method !== 'GET') {
    config.body = body instanceof FormData ? body : JSON.stringify(body)
  }

  try {
    const response = await fetch(fullUrl, config)
    const status = response.status
    const data = await handleResponse<T>(response)

    return {
      data,
      status,
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    // Erreur réseau ou autre
    throw new ApiError(
      error instanceof Error ? error.message : 'Erreur réseau inconnue',
      0
    )
  }
}

/**
 * Méthodes helper pour les différents types de requêtes
 */
export const api = {
  get: <T>(endpoint: string, params?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body }),

  /** POST avec FormData (fichier binaire, pas base64). Ne pas set Content-Type. */
  postForm: <T>(endpoint: string, formData: FormData) =>
    apiRequest<T>(endpoint, { method: 'POST', body: formData }),

  put: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'PUT', body }),

  /** PUT avec FormData (fichier binaire, pas base64). Ne pas set Content-Type. */
  putForm: <T>(endpoint: string, formData: FormData) =>
    apiRequest<T>(endpoint, { method: 'PUT', body: formData }),

  patch: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body }),

  delete: <T>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'DELETE' }),
}

